<template>
    <div class="summary-container" :class="{ 'pdf-shadow-fix': applyPdfShadowFix }">
        <template v-for="summaryItem in summaryItems">
            <div class="summary-item">
                <Text as="span" size="f-8">{{ summaryItem.title }}</Text>
                <Spacer height="0.375rem" />
                <div class="statistic">
                    <Text as="h5" weight="600" style="letter-spacing: -0.06rem">
                        <template v-if="typeof summaryItem.content === 'number'">
                            <span v-if="summaryItem.isPercent">
                                <Percent
                                    :value="summaryItem.content"
                                    :decimal-places="summaryItem.decimalPlaces"
                                />
                            </span>
                            <span v-else>
                                <NumberVue
                                    :value="summaryItem.content"
                                    :decimal-places="summaryItem.decimalPlaces"
                                />
                            </span>
                        </template>
                        <span v-else>
                            {{ summaryItem.content }}
                        </span>
                    </Text>
                    <Spacer width="0.75rem" />
                </div>
                <template v-if="summaryItem.includeIcon">
                    <WarnIcon
                        v-if="summaryItem.warningCondition"
                        class="statistic-icon"
                        translucent
                    />
                    <CheckIcon v-else class="statistic-icon" translucent />
                </template>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import {
    CheckIcon,
    Number as NumberVue,
    Percent,
    Spacer,
    Text,
    WarnIcon,
} from '@opteo/components-next'

import type { SummaryItem } from '@/composition/toolkit/scorecard/types'

type Props = {
    summaryItems: SummaryItem[]
    applyPdfShadowFix?: boolean
}

withDefaults(defineProps<Props>(), {
    applyPdfShadowFix: false,
})
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';
@import '@/assets/css/variables.scss';

.summary-container {
    @include container;
    @include br-20;
    @include w-100;
    @include grid;
    grid-template-columns: 1fr 1fr;

    .summary-item {
        @include ph-24;
        @include pv-20;
        border-bottom: 1px solid #f7f7f9;
        @include relative;
    }
    .summary-item:last-child,
    .summary-item:nth-last-child(-n + 2) {
        border-bottom: none;
    }
    .summary-item:nth-child(odd) {
        border-right: 1px solid #f7f7f9;
    }
    .statistic {
        @include flex;
        @include items-center;
    }
}

// Responsive
@media (max-width: $mq-480-max) {
    .summary-container {
        @include block;

        .summary-item,
        .summary-item:nth-last-child(-n + 2) {
            @include ph-24;
            @include pv-20;
            border-bottom: 1px solid #f7f7f9;
        }
        .summary-item:last-child {
            border-bottom: none;
        }
        .summary-item:nth-child(odd) {
            border-right: unset;
        }
    }
}

@media screen and (min-width: $mq-481-min) and (max-width: $mq-767-max) {
    .summary-container {
        @include block;

        .summary-item,
        .summary-item:nth-last-child(-n + 2) {
            @include ph-24;
            @include pv-20;
            border-bottom: 1px solid #f7f7f9;
        }
        .summary-item:last-child {
            border-bottom: none;
        }
        .summary-item:nth-child(odd) {
            border-right: unset;
        }
    }
}
</style>
